/**
 * Form values hook for EntityForm.
 *
 * Manages form value state using a per-instance Zustand store.
 * Provides get/set operations, dirty tracking, and reset functionality.
 *
 * Each call to `useFormValues` creates its own isolated store instance,
 * so multiple EntityForm instances on the same page do not share state.
 *
 * @see Spec section 4.2 for the hook signature and store shape.
 * @see Plan section 3.1 for implementation details.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';

import type { FieldConfig } from '../types';

// ---------------------------------------------------------------------------
// Store Shape
// ---------------------------------------------------------------------------

/**
 * Internal Zustand store shape for form values.
 * Not exported -- consumers interact through the hook return type.
 */
interface FormValuesStore {
  /** Current form values keyed by field name */
  values: Record<string, unknown>;

  /** Initial values the form was seeded with (used for dirty checks and reset) */
  initialValues: Record<string, unknown>;

  /**
   * Bumped by every BULK write (`setValues`, `reset`) in the same store update
   * as the values it writes; `0` for the initial seed. Never bumped by a
   * single-field `setValue`.
   */
  loadGeneration: number;

  /** Update a single field value */
  setValue: (name: string, value: unknown) => void;

  /** Merge multiple field values into the store */
  setValues: (values: Record<string, unknown>) => void;

  /** Reset values to initial state, optionally providing new initial values */
  reset: (newValues?: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// Return Type
// ---------------------------------------------------------------------------

/**
 * Public return type of useFormValues.
 */
export interface UseFormValuesReturn {
  /** All current form values */
  values: Record<string, unknown>;

  /** Get the current value for a single field */
  getValue: (name: string) => unknown;

  /** Set a single field value */
  setValue: (name: string, value: unknown) => void;

  /** Merge multiple field values at once */
  setValues: (values: Record<string, unknown>) => void;

  /** Reset the form to initial values, or to new values if provided */
  reset: (newValues?: Record<string, unknown>) => void;

  /** Whether any field value differs from its initial value */
  isDirty: boolean;

  /** Whether a specific field's value differs from its initial value */
  isFieldDirty: (name: string) => boolean;

  /** The initial values the form was seeded with */
  initialValues: Record<string, unknown>;

  /**
   * Counter bumped by every bulk load — `setValues` (e.g. the edit-mode fetch
   * merge) and `reset` (including the one fired when the `initialValues` prop
   * changes) — and never by a single-field `setValue`. Lets consumers tell
   * loaded / restored values apart from edits: `useFieldConditions`
   * re-baselines its edge-triggered `checked` overlay on a change instead of
   * firing it, so a loaded record is never overwritten.
   */
  loadGeneration: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Shallow-compares two optional records by key count and strict value equality.
 * Returns `true` when both are reference-equal, both undefined, or contain the
 * same keys with identical (===) values.
 */
function shallowEqual(
  a?: Record<string, unknown>,
  b?: Record<string, unknown>,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  /* v8 ignore start */
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  /* v8 ignore stop */
  return true;
}

/**
 * Builds the merged initial values from field defaults and the caller-
 * provided `initialValues` prop.
 *
 * Priority (highest wins):
 * 1. `initialValues` passed by the caller
 * 2. `defaultValue` defined on the FieldConfig
 * 3. `undefined` (field has no initial value)
 */
function buildInitialValues(
  fields: Record<string, FieldConfig>,
  initialValues?: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};

  for (const [name, fieldConfig] of Object.entries(fields)) {
    if (initialValues !== undefined && name in initialValues) {
      merged[name] = initialValues[name];
    } else if (fieldConfig.defaultValue !== undefined) {
      merged[name] = fieldConfig.defaultValue;
    }
    // If neither initialValues nor defaultValue exists for a field,
    // we intentionally leave it absent so `getValue` returns `undefined`.
  }

  // Preserve extra values not declared in fields (e.g. config_* keys
  // managed by custom panels outside the EntityForm field config).
  if (initialValues !== undefined) {
    for (const [name, value] of Object.entries(initialValues)) {
      if (!(name in merged)) {
        merged[name] = value;
      }
    }
  }

  return merged;
}

/**
 * Creates a new Zustand store instance for form values.
 *
 * Extracted to a standalone factory so it can be called from the
 * `useState` lazy initialiser without triggering lint rules about
 * accessing refs during render.
 */
function createFormValuesStore(
  fields: Record<string, FieldConfig>,
  initialValues?: Record<string, unknown>,
): StoreApi<FormValuesStore> {
  const merged = buildInitialValues(fields, initialValues);

  return createStore<FormValuesStore>((set) => ({
    values: { ...merged },
    initialValues: merged,
    loadGeneration: 0,

    setValue: (name, value) => {
      set((state) => ({
        values: { ...state.values, [name]: value },
      }));
    },

    setValues: (incoming) => {
      set((state) => ({
        values: { ...state.values, ...incoming },
        loadGeneration: state.loadGeneration + 1,
      }));
    },

    reset: (newValues) => {
      set((state) => {
        const nextInitial = newValues ?? state.initialValues;
        return {
          values: { ...nextInitial },
          initialValues: nextInitial,
          loadGeneration: state.loadGeneration + 1,
        };
      });
    },
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages form value state using a per-instance Zustand store.
 *
 * Creates a NEW Zustand store on the first render (stabilised via `useState`
 * lazy initialiser) so each hook call -- and therefore each EntityForm --
 * operates on its own isolated state.
 *
 * @param fields        - Field configuration map (used to extract `defaultValue`).
 * @param initialValues - Optional caller-provided initial values. Takes
 *                        precedence over field-level `defaultValue`.
 * @returns Form value state, accessors, mutators, and dirty tracking helpers.
 *
 * @example
 * ```ts
 * const { values, setValue, isDirty, reset } = useFormValues(config.fields, {
 *   firstName: 'Jane',
 *   role: 'admin',
 * });
 * ```
 */
export function useFormValues(
  fields: Record<string, FieldConfig>,
  initialValues?: Record<string, unknown>,
): UseFormValuesReturn {
  // ---- Store creation (once per hook instance) ----
  // Using useState with a lazy initialiser ensures the store is created
  // exactly once and the reference is stable across re-renders without
  // triggering lint rules about accessing refs during render.

  const [store] = useState<StoreApi<FormValuesStore>>(() =>
    createFormValuesStore(fields, initialValues),
  );

  // ---- Sync store when initialValues prop changes ----
  // When initialValues transitions (e.g., from undefined to actual data after
  // an async fetch), reset the store so the form reflects the new data.
  // We use a ref for fields to avoid re-triggering the effect when the fields
  // object reference changes (it's often not memoised by callers).
  // Shallow comparison prevents infinite loops when callers pass non-memoised
  // objects whose values haven't actually changed.
  const initialValuesRef = useRef(initialValues);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  useEffect(() => {
    if (!shallowEqual(initialValues, initialValuesRef.current)) {
      initialValuesRef.current = initialValues;
      const merged = buildInitialValues(fieldsRef.current, initialValues);
      store.getState().reset(merged);
    }
  }, [initialValues, store]);

  // ---- Selectors (subscribe only to the slices we need) ----

  const values = useStore(store, (s) => s.values);
  const storedInitialValues = useStore(store, (s) => s.initialValues);
  const loadGeneration = useStore(store, (s) => s.loadGeneration);
  const setValueAction = useStore(store, (s) => s.setValue);
  const setValuesAction = useStore(store, (s) => s.setValues);
  const resetAction = useStore(store, (s) => s.reset);

  // ---- Derived: dirty tracking ----

  /**
   * Form-level dirty flag.
   * Uses shallow (===) comparison per field between current and initial values.
   */
  const isDirty = useMemo(() => {
    const currentKeys = Object.keys(values);
    const initialKeys = Object.keys(storedInitialValues);

    // If key counts differ, something has changed
    if (currentKeys.length !== initialKeys.length) {
      return true;
    }

    // Check every key in both objects
    const allKeys = new Set(currentKeys.concat(initialKeys));

    for (const key of Array.from(allKeys)) {
      if (values[key] !== storedInitialValues[key]) {
        return true;
      }
    }
    return false;
  }, [values, storedInitialValues]);

  // ---- Callbacks (stable references) ----

  /**
   * Returns the current value for a single field.
   */
  const getValue = useCallback(
    (name: string): unknown => {
      return store.getState().values[name];
    },
    [store],
  );

  /**
   * Compares a single field's current value to its initial value
   * using strict equality (===).
   */
  const isFieldDirty = useCallback(
    (name: string): boolean => {
      const state = store.getState();
      return state.values[name] !== state.initialValues[name];
    },
    [store],
  );

  // ---- Return ----
  // Memoised so the whole return object keeps a stable identity across renders
  // and only changes when an underlying slice actually changes. Consumers
  // (notably `useEntityForm`) depend on this object wholesale in their own
  // memo/callback dependency arrays, so a fresh literal each render would
  // defeat their memoisation.

  return useMemo<UseFormValuesReturn>(
    () => ({
      values,
      getValue,
      setValue: setValueAction,
      setValues: setValuesAction,
      reset: resetAction,
      isDirty,
      isFieldDirty,
      initialValues: storedInitialValues,
      loadGeneration,
    }),
    [
      values,
      getValue,
      setValueAction,
      setValuesAction,
      resetAction,
      isDirty,
      isFieldDirty,
      storedInitialValues,
      loadGeneration,
    ],
  );
}
